import z from "zod";
import { fingerprintDataSchema } from "../dtos/fingerprint-data";
import { baseEntitySchema } from "./base";
import { StatusTierSchema } from "../dtos/status-tier";
import { DealCategory } from "./deal-category";
import { PaymentOption } from "./product";

export const DEAL_STAGES: Record<string, number> = {
  BUY_PROCESS_ORDER: 259476547, // Qualified buy lead // maybe switched
  SELL_PROCESS_ORDER: 264262987, // Qualified sell lead

  BUY_ATTEMPTING_TO_CONTACT: 259476548,
  SELL_ATTEMPTING_TO_CONTACT: 264262988,

  BUY_NEGOTIATION: 259476549,
  SELL_NEGOTIATION: 264262989,

  BUY_PENDING: 259476550, // Customer completed buy transaction
  SELL_PENDING: 264262990, // Customer completed sell transaction

  BUY_WAITING_FOR_BUYER: 259476551,
  SELL_WAITING_FOR_BUYER: 264262991,

  BUY_CLOSED_WON: 259476552,
  SELL_CLOSED_WON: 264262992,

  BUY_BUSINESS_LOST: 259476553,
  SELL_BUSINESS_LOST: 264262993,
};

export const ALLOWED_DEAL_SUBMISSION_STAGES = [
  DEAL_STAGES.BUY_PROCESS_ORDER,
  DEAL_STAGES.SELL_PROCESS_ORDER,
  DEAL_STAGES.BUY_ATTEMPTING_TO_CONTACT,
  DEAL_STAGES.SELL_ATTEMPTING_TO_CONTACT,
  DEAL_STAGES.BUY_NEGOTIATION,
  DEAL_STAGES.SELL_NEGOTIATION,
  // DEAL_STAGES.BUY_PENDING,
  // DEAL_STAGES.SELL_PENDING,
];

export const ALLOWED_DEAL_VERIFY_STAGES = [
  DEAL_STAGES.BUY_PENDING,
  DEAL_STAGES.SELL_PENDING,
]

export const BUSINESS_TYPES: Record<string, string> = {
  BUY: "Sales", // Customer purchasing miles/points from The Miles Market
  SELL: "Purchase", // Customer selling miles/points to The Miles Market
};

export { DealCategory };

// Stage duration tracking schema
export const stageDurationSchema = z.object({
  stage: z.number(),
  enteredAt: z.date(),
  exitedAt: z.date().optional(),
  durationMs: z.number().optional(), // Duration in milliseconds, calculated when exiting stage
});

export type StageDuration = z.infer<typeof stageDurationSchema>;

// Stage history tracking schema
export const stageHistorySchema = z.object({
  currentStage: z.number(),
  currentStageEnteredAt: z.date(),
  stageDurations: z.array(stageDurationSchema).default([]),
  totalTimeInPipeline: z.number().optional(), // Total time from creation to current stage in milliseconds
});

export type StageHistory = z.infer<typeof stageHistorySchema>;

export const dealDataSchema = z.object({
  category: z.nativeEnum(DealCategory),
  name: z.string(),
  customerId: z.string(),
  productId: z.string().optional(),
  productName: z.string(),
  quantity: z.number(),
  amount: z.number().min(1),
  buyOrSell: z.string(),
  type: z.string(),
  stage: z.number(),
  unitPrice: z.number(),
  businessLostReason: z.string().optional(),
  accountEmail: z.string().optional(),
  accountPassword: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentDetails: z.string().optional(),
  paypalEmail: z.string().optional(),
  fingerprints: z.array(fingerprintDataSchema),
  statusTier: StatusTierSchema.optional(),
  // Stage duration tracking
  stageHistory: stageHistorySchema,
  enteredPending: z.boolean().default(false),

  sourceTemporaryDealId: z.string().optional(),
  paymentOption: z.nativeEnum(PaymentOption).optional(),
  promoCode: z.object({
    id: z.string(),
    code: z.string(),
    discount: z.number(),
    name: z.string(),
  }).optional(),
});

export type DealData = z.infer<typeof dealDataSchema>;
export const dealSchema = baseEntitySchema.merge(dealDataSchema);
export type Deal = z.infer<typeof dealSchema>;
